import { type AtlasRead, drawAtlasRead } from "./atlasReadLogic";
import type { AtlasAnswer, AtlasJob } from "./atlasReadWorker";

/*
    The bakes off the game's frames (0.98.8, the owner: "please look, in detail, into ways that can lower the lag...
    find whatever blocks"): a normal map (managers/normalLook.ts) or a snow cap (managers/snowLook.ts) is baked from
    its picture as its atlas page holds it. Drawn on the page's own thread, a picture off a page made the browser decode
    the whole page first whenever it had let its decoded copy go (a 4096-pixel page: a tenth of a second and more on a
    fast computer, a second and a half in the lab), and the game froze for it, again and again as the bakes went from
    page to page. Now the picture is read and baked in a worker (managers/atlasReadWorker.ts): the page's file decoded
    there, the picture drawn there as before and the bake's sums done there; the page only puts the pixels it gets back
    into a canvas and a texture, as before. Where there's no worker (no OffscreenCanvas, a page that isn't one of the
    game's own files, a worker that won't start or a job that failed), the bake is done on the page as it was.
*/

type Spec = AtlasJob extends infer J ? J extends AtlasJob ? Omit<J, "id" | "url"> : never : never;

/** a bake's canvas (its size, its pixels put in) on the page, as the bakes have always had theirs (read often: kept off the card) */
export function bakeCanvas(w: number, h: number): { readonly canvas: HTMLCanvasElement, readonly ctx: CanvasRenderingContext2D } | undefined {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    return ctx ? { canvas, ctx } : undefined;
}

/** a picture read on the page itself, off its page, into a canvas of its own (as the bakes always were) */
export function readHere(page: CanvasImageSource, read: AtlasRead): { readonly canvas: HTMLCanvasElement, readonly ctx: CanvasRenderingContext2D, readonly data: ImageData } | undefined {
    const made = bakeCanvas(read.w, read.h);
    if (!made) return undefined;
    return { ...made, data: drawAtlasRead(made.ctx, page, read) };
}

/** the pixels (RGBA) put into a canvas of the bake's size */
export function canvasWith(pixels: Uint8ClampedArray, w: number, h: number): HTMLCanvasElement | undefined {
    const made = bakeCanvas(w, h);
    if (!made) return undefined;
    const out = made.ctx.createImageData(w, h);
    out.data.set(pixels);
    made.ctx.putImageData(out, 0, 0);
    return made.canvas;
}

class AtlasReadsClass {
    private _worker?: Worker;
    /** the worker wouldn't start, or its script failed: every bake is done on the page from then on */
    private _broken = false;
    private _next = 0;
    private readonly _waiting = new Map<number, { readonly resolve: (pixels: Uint8ClampedArray | undefined) => void, readonly reject: (error: Error) => void }>();

    /** the jobs sent and not answered yet */
    get busy(): number { return this._waiting.size; }

    /**
     * whether the worker can bake off this page: an image loaded from one of the game's own files (its address the
     * game's: the worker fetches it), where workers can draw (OffscreenCanvas) and decode (createImageBitmap)
     */
    canRead(resource: unknown): resource is HTMLImageElement {
        if (this._broken) return false;
        if (typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined" || typeof createImageBitmap === "undefined") return false;
        if (typeof HTMLImageElement === "undefined" || !(resource instanceof HTMLImageElement) || !resource.complete) return false;
        const src = resource.currentSrc || resource.src;
        try {
            const url = new URL(src, location.href);
            return (url.protocol === "https:" || url.protocol === "http:") && url.origin === location.origin;
        } catch {
            return false;
        }
    }

    /** a bake done in the worker: its pixels (none: nothing to show), or a failure (the page bakes it itself then) */
    bake(page: HTMLImageElement, spec: Spec): Promise<Uint8ClampedArray | undefined> {
        const worker = this._start();
        if (!worker) return Promise.reject(new Error("no worker"));
        const id = this._next++;
        return new Promise((resolve, reject) => {
            this._waiting.set(id, { resolve, reject });
            const job = { ...spec, id, url: new URL(page.currentSrc || page.src, location.href).href } as AtlasJob;
            try {
                worker.postMessage(job);
            } catch (e) {
                this._waiting.delete(id);
                reject(e instanceof Error ? e : new Error(String(e)));
            }
        });
    }

    private _start(): Worker | undefined {
        if (this._worker || this._broken) return this._worker;
        try {
            const worker = new Worker(new URL("./atlasReadWorker.ts", import.meta.url), { type: "module", name: "atlas-bakes" });
            worker.onmessage = (event: MessageEvent<AtlasAnswer>) => {
                const answer = event.data;
                const waiting = this._waiting.get(answer.id);
                if (!waiting) return;
                this._waiting.delete(answer.id);
                if (answer.error !== undefined) waiting.reject(new Error(answer.error));
                else waiting.resolve(answer.pixels);
            };
            // (its script didn't load or broke: what it was asked is baked on the page, and so is everything after)
            worker.onerror = event => {
                event.preventDefault();
                this._fail(new Error(event.message || "the bakes' worker failed"));
            };
            worker.onmessageerror = () => this._fail(new Error("a bake's answer couldn't be read"));
            this._worker = worker;
        } catch {
            this._broken = true;
        }
        return this._worker;
    }

    private _fail(error: Error): void {
        this._broken = true;
        this._worker?.terminate();
        this._worker = undefined;
        for (const waiting of this._waiting.values()) waiting.reject(error);
        this._waiting.clear();
    }
}

export const AtlasReads = new AtlasReadsClass();
